import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const getToken = () => localStorage.getItem("access_token") || localStorage.getItem("token");

const BROKERS = ["Exness", "XM", "FBS", "Tickmill", "IC Markets", "Pepperstone", "FXTM", "OctaFX", "Other"];

const LinkAccount = () => {
  const navigate = useNavigate();
  const [accountRole, setAccountRole] = useState("trader");
  const [broker, setBroker] = useState("Exness");
  const [server, setServer] = useState("");
  const [platform, setPlatform] = useState("MT5");
  const [accountNumber, setAccountNumber] = useState("");
  const [investorPassword, setInvestorPassword] = useState("");
  const [nickname, setNickname] = useState("");
  const [about, setAbout] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async () => {
    if (!agree) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const res = await fetch("/api/trader/accounts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          account_number: accountNumber,
          broker,
          platform,
          server,
          investor_password: investorPassword,
          account_role: accountRole,
          nickname: nickname,
          about: about,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || "Failed to link account");
      setSuccess("Account linked successfully! Redirecting to dashboard...");
      setTimeout(() => navigate("/trader"), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>Link Existing Broker Account</h2>
          <p className="muted">Connect your broker account to CrunchAlpha for performance tracking, copy-trading, and revenue sharing.</p>
        </div>
      </div>

      {error && <div className="card" style={{ borderLeft: "3px solid #ef4444", marginBottom: 16, padding: "10px 16px" }}>
        <span style={{ color: "var(--danger)", fontSize: 13 }}>{error}</span>
      </div>}
      {success && <div className="card" style={{ borderLeft: "3px solid #10b981", marginBottom: 16, padding: "10px 16px" }}>
        <span style={{ color: "var(--success)", fontSize: 13 }}>{success}</span>
      </div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title-row"><h3>1. Role & Broker</h3></div>
        <div className="form-grid">
          <div className="form-group">
            <div className="filter-label">Use this account as</div>
            <div className="chip-group" style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {["trader", "investor", "analyst"].map(r => (
                <button key={r} type="button"
                  onClick={() => setAccountRole(r)}
                  style={{
                    padding: "6px 16px", borderRadius: 6, border: "1px solid",
                    borderColor: accountRole === r ? "#667eea" : "rgba(255,255,255,0.15)",
                    background: accountRole === r ? "rgba(102,126,234,0.15)" : "transparent",
                    color: accountRole === r ? "#667eea" : "inherit",
                    cursor: "pointer", fontSize: 13, textTransform: "capitalize"
                  }}>
                  {r === "trader" ? "  " : r === "investor" ? "" : ""} {r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="filter-label">
              Broker
              <select className="filter-input" value={broker} onChange={e => setBroker(e.target.value)}>
                {BROKERS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
          </div>

          <div className="form-group">
            <label className="filter-label">
              Platform
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {["MT4", "MT5"].map(p => (
                  <button key={p} type="button"
                    onClick={() => setPlatform(p)}
                    style={{
                      padding: "6px 24px", borderRadius: 6, border: "1px solid",
                      borderColor: platform === p ? "#667eea" : "rgba(255,255,255,0.15)",
                      background: platform === p ? "rgba(102,126,234,0.15)" : "transparent",
                      color: platform === p ? "#667eea" : "inherit",
                      cursor: "pointer", fontSize: 13, fontWeight: platform === p ? 600 : 400
                    }}>
                    {p}
                  </button>
                ))}
              </div>
            </label>
          </div>

          <div className="form-group">
            <label className="filter-label">
              Server / Group
              <input className="filter-input" type="text" value={server}
                onChange={e => setServer(e.target.value)}
                placeholder="Sign inkan nama server broker (contoh: FBS-Real5, Dupoin-Real)" />
            </label>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title-row"><h3>2. Account & Investor Password</h3></div>
        <div className="form-grid">
          <div className="form-group">
            <label className="filter-label">
              Account Number
              <input className="filter-input" type="text" value={accountNumber}
                onChange={e => setAccountNumber(e.target.value)}
                placeholder="Sign inkan nomor akun MT4/MT5 Anda" />
            </label>
          </div>

          <div className="form-group">
            <label className="filter-label">
              Investor Password (read-only)
              <input className="filter-input" type="password" value={investorPassword}
                onChange={e => setInvestorPassword(e.target.value)}
                placeholder="Sign inkan investor password (read-only)" />
            </label>
            <p className="muted">CrunchAlpha only needs read-only access to pull trade history and calculate AlphaRank™. We cannot execute trades with this password.</p>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title-row"><h3>3. Account Name & Description (Optional)</h3></div>
        <div className="form-grid">
          <div className="form-group">
            <label className="filter-label">
              Account Nickname
              <input className="filter-input" type="text" value={nickname}
                onChange={e => setNickname(e.target.value)}
                placeholder="Your strategy name (e.g., Gold Swing Strategy)" />
            </label>
            <p className="muted">This name will appear in the Marketplace as your account identity.</p>
          </div>
          <div className="form-group">
            <label className="filter-label">
              About This Account
              <textarea className="filter-input" rows={3} value={about}
                onChange={e => setAbout(e.target.value)}
                placeholder="Describe your trading strategy (e.g., Swing trading Gold with strict risk management, SL on every trade)"
                style={{ resize: "vertical", minHeight: 80 }} />
            </label>
            <p className="muted">This description helps investors understand your strategy in the Marketplace.</p>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-title-row"><h3>4. IB & Risk Acknowledgement</h3></div>
        <label className="checkbox-label" style={{ marginBottom: 16 }}>
          <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} />
          <span>I agree to link this account to CrunchAlpha IB for rebate, revenue sharing, and copy-trading integration as per the agreement.</span>
        </label>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="primary-btn"
            disabled={!agree || !accountNumber || !investorPassword || !server || busy}
            onClick={handleSubmit}>
            {busy ? "Linking..." : "Link Account"}
          </button>
        </div>
      </div>
    </div>
  );
};
export default LinkAccount;
